import './Balance.css';
import { subscriptionBlockReason } from '../lib/billing-availability';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { REEL_PACKAGES, type BillingSummary, type PaymentProvider } from '@reels/shared/billing';
import { api, errorMessage, isDemo } from '../lib/api';
import { ActionButton } from '../components/ActionButton';
import { Disclosure } from '../components/Disclosure';
import { OfflineBanner } from '../components/States';
import * as telegram from '../lib/telegram';
import { useActions } from '../state/actions';
import { useStore } from '../state/store';
import { formatDate, formatNumber, ls, t } from '../i18n';

const money = (value: number, provider: PaymentProvider): string => formatNumber(value) + (provider === 'stars' ? ' ⭐' : ' ₽');
const date = (value: string): string => formatDate(value, { day: 'numeric', month: 'long', year: 'numeric' });
function DocLink({ url, children }: { url: string | null; children: string }): ReactElement {
  return url ? <a href={url} onClick={event => { event.preventDefault(); telegram.openLink(url); }}>{children}</a> : <span>{children}</span>;
}

function PromoCode({ onRedeemed }: { onRedeemed: (billing: BillingSummary, videos: number) => void }): ReactElement {
  const { state } = useStore();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const redeem = async () => {
    setBusy(true); setError(null);
    try {
      const result = await api.redeemPromo(code.trim());
      telegram.hapticFeedback.notification('success');
      setCode('');
      onRedeemed(result.billing, result.videos);
    } catch (err) { telegram.hapticFeedback.notification('error'); setError(errorMessage(err)); }
    finally { setBusy(false); }
  };
  return <form className="billing-promo" onSubmit={event => { event.preventDefault(); void redeem(); }}>
    <input type="text" value={code} maxLength={32} autoCapitalize="characters" autoComplete="off" autoCorrect="off" spellCheck={false}
      aria-label={t('balance.promo.label')} placeholder="PROMOCODE" onChange={event => setCode(event.target.value)} />
    {error ? <p className="small billing-promo-error" role="alert">{error}</p> : null}
    <button type="submit" className="btn" disabled={!state.online || busy || code.trim().length < 3}>{busy ? t('balance.promo.checking') : t('balance.promo.activate')}</button>
  </form>;
}

export function Balance({ exhausted = false }: { exhausted?: boolean }): ReactElement {
  const { state, dispatch } = useStore();
  const { loadProfile, toast } = useActions();
  const [billing, setBilling] = useState<BillingSummary | null>(null);
  const [selectedId, setSelectedId] = useState('active');
  const [selectedAddonId, setSelectedAddonId] = useState('addon');
  const [changingPlan, setChangingPlan] = useState(false);
  const [checkingCancellation, setCheckingCancellation] = useState(false);
  const [provider, setProvider] = useState<PaymentProvider>('tribute');
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [waiting, setWaiting] = useState(false);
  // Tribute opens outside the app and never reports back, so we only poll and keep the button usable.
  const [watching, setWatching] = useState(false);
  const pending = useRef<{ baseline: string | null } | null>(null);
  const buying = useRef(false);
  const refresh = useCallback(async () => {
    try {
      const result = await api.billing();
      setBilling(result);
      if (!result.subscription || (!result.subscription.changeRequiresCancellation && result.subscription.cancelled)) setCheckingCancellation(false);
      const payment = result.history.find(item => item.kind === 'purchase')?.id ?? null;
      if (pending.current && payment !== pending.current.baseline) {
        pending.current = null; setWaiting(false); setWatching(false); setChangingPlan(false); setAgreed(false);
        setNotice(t('balance.paid.confirmed'));
        void loadProfile();
      }
    } catch (err) { setError(errorMessage(err)); }
  }, [loadProfile]);
  useEffect(() => {
    void refresh();
    const onReturn = () => { if (document.visibilityState === 'visible') void refresh(); };
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    const timer = waiting || watching || checkingCancellation ? window.setInterval(onReturn, 3000) : undefined;
    return () => { window.removeEventListener('focus', onReturn); document.removeEventListener('visibilitychange', onReturn); window.clearInterval(timer); };
  }, [refresh, waiting, watching, checkingCancellation]);
  useEffect(() => { if (!state.profile) void loadProfile(); }, [loadProfile, state.profile]);

  const packages: BillingSummary['packages'] = billing?.packages ?? REEL_PACKAGES.map(pack => ({ ...pack, tribute: false }));
  const subscription = billing?.subscription;
  const changeMode = !!subscription && changingPlan;
  const addonOnly = !!subscription && !changeMode;
  const addons = billing?.addons ?? (billing ? [{ ...billing.addon, id: 'addon' }] : []);
  const availableAddons = addons.filter(offer => offer.available);
  const selectedAddon = availableAddons.find(offer => offer.id === selectedAddonId) ?? availableAddons[0];
  const buyAddon = addonOnly && !!selectedAddon;
  const activePackage = subscription ? packages.find(pack => pack.id === subscription.packageId) : undefined;
  const currentRank = packages.findIndex(pack => pack.id === subscription?.packageId);
  const next = subscription ? packages[currentRank + 1] : packages[1] ?? packages[0];
  const planOptions = changeMode ? packages.filter(pack => pack.id !== subscription?.packageId) : packages;
  const selected = (exhausted && !changeMode ? next : planOptions.find(pack => pack.id === selectedId)) ?? planOptions[0] ?? packages[0]!;
  const method: PaymentProvider = changeMode ? subscription.provider ?? 'stars' : billing?.tributeEnabled && (!billing.starsEnabled || provider === 'tribute') ? 'tribute' : 'stars';
  const addonProvider: PaymentProvider = method === 'tribute' && selectedAddon?.tributeAvailable
    ? 'tribute' : selectedAddon?.starsAvailable ? 'stars' : 'tribute';
  const needsCancellation = changeMode && subscription.provider === 'tribute'
    && (subscription.changeRequiresCancellation ?? !subscription.cancelled);
  const blockedReason = subscriptionBlockReason({ billing, packageId: selected.id, provider: method,
    online: state.online, agreed, busy, waiting,
    documentsReady: !!state.profile?.links.terms && !!state.profile?.links.privacy });
  const purchase = async (addon = false) => {
    if (buying.current) return;
    buying.current = true; setBusy(true); setError(null); setNotice(null);
    try {
      if (isDemo()) throw new Error(t('balance.paid.demo'));
      if (!agreed) throw new Error(t('balance.paid.agreeFirst'));
      pending.current = { baseline: billing?.history.find(item => item.kind === 'purchase')?.id ?? null };
      const checkout = await api.checkout(addon ? selectedAddon!.id : selected.id, addon ? addonProvider : method);
      if (checkout.provider === 'stars') {
        setWaiting(true);
        const result = await telegram.openInvoice(checkout.url);
        if (result === 'cancelled' || result === 'failed') {
          pending.current = null; setWaiting(false);
          setNotice(result === 'cancelled' ? t('balance.paid.cancelled') : t('balance.paid.failed'));
        } else setNotice(t('balance.paid.checking'));
      } else {
        telegram.openLink(checkout.url); setWatching(true);
        setNotice(t('balance.paid.tributeOpened'));
      }
      await refresh();
    } catch (err) { pending.current = null; setWaiting(false); setWatching(false); setError(errorMessage(err)); }
    finally { buying.current = false; setBusy(false); }
  };
  /** Объясняет, почему платить пока нельзя; true — оплату не начинаем. */
  const blocked = (reason: string | null): boolean => {
    if (!reason) return false;
    telegram.hapticFeedback.notification('error');
    toast(reason);
    return true;
  };
  return <>
    <h1>{exhausted ? t('balance.title.exhausted') : t('balance.title.default')}</h1>
    {!state.online ? <OfflineBanner /> : null}
    {billing && !billing.enabled ? <div className="banner info">{isDemo() ? t('balance.banner.demo') : t('balance.banner.soon')}</div> : null}
    {error ? <div className="banner warning" role="alert">{error} <button className="btn" onClick={() => { setError(null); void refresh(); }}>{t('balance.refresh')}</button></div> : null}
    {waiting ? <div className="card"><p>{t('balance.waiting.text')}</p>
      <button className="btn" onClick={() => void refresh()}>{t('balance.waiting.check')}</button>
      <button className="btn" onClick={() => { pending.current = null; setWaiting(false); setNotice(null); }}>{t('balance.waiting.back')}</button>
    </div> : null}
    {notice ? <div className="banner info" role="status">{notice}</div> : null}
    <div className="tile balance-tile"><div className="col grow"><span className="tile-label">{t('balance.available')}</span><strong className="balance-value">{billing?.available ?? '…'}</strong></div></div>
    {subscription ? <section className="card billing-active-subscription" aria-label={t('balance.sub.aria')}>
      <div className="billing-active-header">
        <span className="billing-active-status"><span className="billing-active-dot" aria-hidden="true" />{subscription.cancelled ? t('balance.sub.untilEnd') : t('balance.sub.active')}</span>
        <span className="billing-active-provider">{subscription.provider === 'tribute' ? 'Tribute' : subscription.provider === 'stars' ? 'Telegram Stars' : t('balance.sub.providerFallback')}</span>
      </div>
      <div className="billing-active-plan">
        <div className="billing-active-name"><span>{t('balance.sub.yourPlan')}</span><strong>{activePackage ? `«${ls(activePackage.title)}»` : t('balance.sub.activePlan')}</strong></div>
        {activePackage ? <span className="billing-active-limit">{t(subscription.provider === 'tribute' ? 'balance.sub.limitMonthly' : 'balance.sub.limit30', { n: activePackage.videos })}</span> : null}
      </div>
      <div className="billing-active-period"><span>{subscription.cancelled ? t('balance.sub.accessUntil') : t('balance.sub.paidUntil')}</span><strong>{date(subscription.periodEnd)}</strong></div>
      {subscription.cancelled ? <p className="billing-active-note">{t('balance.sub.cancelledNote')}</p> : null}
      <div className="billing-active-actions">
        <button type="button" className="btn block" disabled={busy || waiting} onClick={() => {
          setChangingPlan(!changeMode); setAgreed(false); setError(null); setNotice(null);
          if (changeMode) setCheckingCancellation(false);
          else setSelectedId(next?.id ?? packages.find(pack => pack.id !== subscription.packageId)?.id ?? selectedId);
        }}>{t(changeMode ? 'balance.change.back' : 'balance.change.open')}</button>
        {subscription.provider === 'tribute' && !changeMode ? <button type="button" className="btn block" onClick={() => telegram.openLink('https://t.me/tribute')}>{t('balance.sub.manage')}</button> : null}
      </div>
    </section> : null}
    {billing && billing.reserved > 0 ? <p className="small muted">{t('balance.reserved', { n: billing.reserved })}</p> : null}
    {buyAddon ? <span className="section-title">{t('balance.addon.title')}</span> : null}
    {addonOnly ? availableAddons.map(offer => {
      const offerProvider: PaymentProvider = method === 'tribute' && offer.tributeAvailable
        ? 'tribute' : offer.starsAvailable ? 'stars' : 'tribute';
      return <button key={offer.id} type="button" className="card selectable billing-addon-card" aria-pressed={selectedAddon?.id === offer.id}
        onClick={() => { setSelectedAddonId(offer.id); telegram.hapticFeedback.selection(); }}>
        <span className="billing-addon-copy"><strong>{t('balance.addon.videos', { n: offer.videos })}</strong></span>
        <span className="billing-addon-buy"><strong>{money(offerProvider === 'tribute' ? offer.rub : offer.stars, offerProvider)}</strong></span>
      </button>;
    }) : null}
    {buyAddon ? <p className="small muted">{t('balance.addon.note')}</p> : null}
    {changeMode || (!exhausted && !subscription) ? <>
      <span className="section-title">{t(changeMode ? 'balance.change.choose' : 'balance.packages.title')}</span>
      {planOptions.map(pack => <button type="button" key={pack.id} className="card selectable package billing-package" aria-pressed={!buyAddon && selected.id === pack.id}
        onClick={() => { setSelectedId(pack.id); telegram.hapticFeedback.selection(); }}>
        {pack.popular ? <span className="badge">{t('balance.packages.popular')}</span> : null}
        <span className="billing-package-details"><strong>{ls(pack.title)}</strong><span>{t(method === 'tribute' ? 'balance.packages.perMonth' : 'balance.packages.per30', { n: pack.videos })}</span></span>
          <span className="package-price"><strong>{money(method === 'stars' ? pack.stars : pack.rub,method)}</strong>
            <span className="small muted">{t('balance.packages.perVideo', { price: money(Math.round((method === 'stars' ? pack.stars : pack.rub) / pack.videos), method) })}</span></span>
      </button>)}
    </> : null}
    {changeMode ? <p className="small muted">{t(subscription.provider === 'tribute' ? 'balance.change.termsTribute' : 'balance.change.termsStars')}</p> : null}
    {needsCancellation ? <div className="card">
      <p className="small">{t('balance.change.cancelFirst')}</p>
      <button type="button" className="btn" disabled={busy || !state.online} onClick={() => void refresh()}>{t('balance.change.check')}</button>
    </div> : null}
    {addonOnly && !buyAddon ? <p className="small muted" role="status">{t('balance.addon.unavailable')}</p> : null}
    {(!addonOnly || buyAddon) && !needsCancellation ? <>{!changeMode ? <><span className="section-title">{t('balance.method.title')}</span>
    <div className="card billing-methods" role="radiogroup" aria-label={t('balance.method.title')}>
      <label className="billing-method"><input type="radio" name="provider" disabled={!billing?.tributeEnabled} checked={method==='tribute'} onChange={() => setProvider('tribute')} />
        <span>{t('balance.method.tribute')}</span></label>
      <label className="billing-method"><input type="radio" name="provider" disabled={!billing?.starsEnabled} checked={method==='stars'} onChange={() => setProvider('stars')} /><span>Telegram Stars</span></label>
    </div></> : null}
    <label className="agree billing-agree"><input type="checkbox" checked={agreed} onChange={event => setAgreed(event.target.checked)} />
      {method === 'tribute' && buyAddon ? <span>{t('balance.agree.addon')}</span>
        : method === 'tribute' ? <span>{t('balance.agree.tribute')}</span> : <span>{t('balance.agree.prefix')}<DocLink url={state.profile?.links.terms ?? null}>{t('balance.agree.terms')}</DocLink>{t('balance.agree.and')}<DocLink url={state.profile?.links.privacy ?? null}>{t('balance.agree.privacy')}</DocLink>{t('balance.agree.end')}</span>}
    </label>
    {!changeMode ? <p className="billing-note">{t(buyAddon ? 'balance.note.addon' : 'balance.note.sub')}</p> : null}</> : null}
    {exhausted && !subscription && next ? <p className="small muted">{t('balance.packages.exhaustedInfo', { n: selected.videos, price: money(method === 'stars' ? selected.stars : selected.rub, method), per: money(Math.round((method === 'stars' ? selected.stars : selected.rub) / selected.videos), method) })}</p> : null}
    {watching ? <p className="small muted" role="status">{t('balance.watching')}</p> : null}
    {exhausted && !subscription ? <button className="btn" onClick={() => dispatch({ type:'route',screen:'balance' })}>{t('balance.allPlans')}</button> : null}
    <div className="disclosures">
      <Disclosure title={t('balance.disclosure.promo')}>
        <PromoCode onRedeemed={(result, videos) => { setBilling(result); setError(null); setNotice(t('balance.promo.activated', { n: videos })); void loadProfile(); }} />
      </Disclosure>
      <Disclosure title={t('balance.disclosure.terms')}>
        <p>{t('balance.terms.carry')}</p>
        <p>{t('balance.terms.addon')}</p>
        <p>{t('balance.terms.one')}</p>
      </Disclosure>
    </div>
    {/* Кнопка оплаты всегда зелёная: если платить ещё рано, по нажатию объясняем почему. */}
    {buyAddon && selectedAddon ? <ActionButton alwaysActive loading={busy}
      onClick={() => blocked(!state.online ? t('balance.block.offline') : waiting ? t('balance.block.pending') : !agreed ? t('balance.block.agree') : null) || void purchase(true)}
      text={t('balance.cta.addon', { n: selectedAddon.videos, price: money(addonProvider === 'tribute' ? selectedAddon.rub : selectedAddon.stars, addonProvider) })} /> : null}
    {needsCancellation ? <ActionButton alwaysActive onClick={() => {
      if (blocked(!state.online ? t('balance.block.offline') : null)) return;
      telegram.openLink('https://t.me/tribute'); setCheckingCancellation(true);
    }} text={t('balance.change.cancelInTribute')} /> : null}
    {!addonOnly && !buyAddon && !needsCancellation && (changeMode || !exhausted || next) ? <ActionButton alwaysActive loading={busy}
      onClick={() => blocked(blockedReason) || void purchase()}
      text={changeMode ? t('balance.change.pay', { plan: ls(selected.title), price: money(method === 'stars' ? selected.stars : selected.rub, method) }) : exhausted ? t('balance.cta.exhausted', { n: selected.videos, price: money(method==='stars' ? selected.stars : selected.rub,method) }) : t(method === 'tribute' ? 'balance.cta.subscribeMonth' : 'balance.cta.subscribe30', { price: money(method==='stars' ? selected.stars : selected.rub,method) })} /> : null}
  </>;
}
